import type { WizardCounts, WizardEntry } from '../questions/answer-wizard-model';
import { showSkipButton } from './build-questions-card';
import type { CodingQuestionAnswer, TranscriptItem } from './coding-events';

/**
 * The Builder's wiring around the answer wizard (FRONT-3155): the open question
 * batch is answered from a panel attached to the reply box, and these pure
 * rules decide what the one Send button does. Pure because the frontend suite
 * runs under plain node — a rule only a click could prove is a rule nobody
 * proves.
 */

type QuestionsItem = Extract<TranscriptItem, { kind: 'questions' }>;

/** Skip is an interview escape hatch: never on a gate that needs an explicit
 *  choice — the publish gate (`requiresOption`), the go-ahead (`confirmsBuild`)
 *  or a consent card. The server refuses it there too; this only keeps the
 *  button from offering what would be refused. */
export function panelAllowsSkip(item: Pick<QuestionsItem, 'mode' | 'confirmsBuild' | 'requiresOption' | 'consentCard'>): boolean {
  return (
    showSkipButton(item.mode, item.confirmsBuild ?? false) &&
    !(item.requiresOption ?? false) &&
    !(item.consentCard ?? false)
  );
}

/** Whether typed words can be an answer of their own (the auto-added "Other"
 *  lives in the reply box, not on the panel). The publish gate takes none. */
export function panelTakesTypedAnswer(item: Pick<QuestionsItem, 'requiresOption'>): boolean {
  return !(item.requiresOption ?? false);
}

/** A batch split off by a partial answer has no run waiting on it. */
export function isDetachedBatch(item: Pick<QuestionsItem, 'remainderOf'>): boolean {
  return typeof item.remainderOf === 'number';
}

export interface SendGate {
  disabled: boolean;
  /** Visible text for WHY Send is unavailable — present iff `disabled`. */
  reason?: string;
}

/**
 * Whether Send is available while a question batch is open, and if not, why.
 * `disabled` is DERIVED from the reason (FRONT-1670): a state is disabled iff
 * it has something to say, so a new reason cannot ship without its sentence.
 *
 * - a detached batch (a partial answer's leftovers) while the run is still
 *   working: nothing is waiting on it and the server refuses it
 * - nothing answered or skipped and nothing typed: nothing to send
 * Anything else sends: chosen entries go out (typed text is their note), or —
 * with nothing chosen — the typed text is the reply.
 */
export function answerSendGate(input: {
  counts: Pick<WizardCounts, 'answered' | 'skipped'>;
  typedText: string;
  detachedAndBusy: boolean;
}): SendGate {
  let reason: string | undefined;
  if (input.detachedAndBusy) {
    reason = 'EDDY is still working. Answer when it’s done.';
  } else if (input.counts.answered + input.counts.skipped === 0 && input.typedText.trim().length === 0) {
    reason = 'Pick an answer or type one to send.';
  }
  return reason ? { disabled: true, reason } : { disabled: false };
}

/**
 * The reply box's placeholder while a question batch is open. It is where the
 * "nothing chosen, nothing typed" reason for a disabled Send is shown, so it
 * must say what to do: pick, or type. Once something is chosen, typing only
 * adds a note.
 */
export function answerPlaceholder(input: {
  counts: Pick<WizardCounts, 'answered' | 'skipped'>;
  takesTypedAnswer: boolean;
}): string {
  if (input.counts.answered + input.counts.skipped > 0) return 'Add a note (optional)…';
  return input.takesTypedAnswer ? 'Pick an answer above, or type your own…' : 'Pick an answer above…';
}

export type ComposerAnswerPlan =
  | { kind: 'answers'; body: { answers: WizardEntry[]; note?: string } }
  | { kind: 'typed-as-answer'; body: { answers: CodingQuestionAnswer[] } }
  | { kind: 'plain-message' };

/**
 * What Send does with an open batch. ONE rule, stated once: if any option was
 * chosen or any question skipped, the entries go out and the typed text rides
 * along as the person's NOTE; if none was, the typed text is the reply — it
 * answers a batch the run is BLOCKED on as "Other" for every question
 * (FRONT-1308/1708, unchanged), and is an ordinary message otherwise.
 */
export function planComposerSend(input: {
  entries: WizardEntry[];
  text: string;
  questionCount: number;
  blocking: boolean;
}): ComposerAnswerPlan {
  const text = input.text.trim();
  if (input.entries.some((e) => e !== null)) {
    return { kind: 'answers', body: { answers: input.entries, ...(text ? { note: text } : {}) } };
  }
  if (input.blocking) {
    return {
      kind: 'typed-as-answer',
      body: { answers: Array.from({ length: input.questionCount }, () => ({ selected: [], other: text })) },
    };
  }
  return { kind: 'plain-message' };
}
